import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Binoculars, WifiSlash } from "@phosphor-icons/react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { FitBounds } from "@/components/MapHelpers";
import { API } from "@/config/constants";
import EmptyState from "@/components/EmptyState";

const MapPage = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMapEntries = async () => {
      try {
        const response = await axios.get(`${API}/map-entries`);
        setEntries(response.data);
      } catch (error) {
        toast.error("Failed to load map data");
      } finally {
        setLoading(false);
      }
    };
    fetchMapEntries();
  }, []);

  const defaultCenter = [40.7128, -74.0060];
  const center = entries.length > 0
    ? [
        entries.reduce((sum, e) => sum + e.latitude, 0) / entries.length,
        entries.reduce((sum, e) => sum + e.longitude, 0) / entries.length
      ]
    : defaultCenter;

  return (
    <div className="max-w-7xl mx-auto page-content" data-testid="map-page">
      <div className="flex flex-col gap-3 mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-white">Your Nature Map</h1>
        <div className="grid grid-cols-2 sm:flex gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate("/offline-maps")} className="bg-[#55624F] border-[#55624F] text-white text-xs sm:text-sm justify-center" data-testid="offline-maps-btn">
            <WifiSlash size={16} className="mr-1" />Offline Maps
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate("/ebird")} className="bg-[#55624F] border-[#55624F] text-white text-xs sm:text-sm justify-center" data-testid="ebird-explorer-btn">
            <Binoculars size={16} className="mr-1" />eBird
          </Button>
        </div>
      </div>

      {loading ? (
        <Skeleton className="h-[500px] w-full bg-[#EAE7DE] rounded-lg" />
      ) : entries.length === 0 ? (
        <EmptyState
          message="No entries with locations yet. Add GPS coordinates to your entries to see them on the map!"
        />
      ) : (
        <div className="h-[calc(100vh-250px)] min-h-[300px] max-h-[600px] rounded-lg overflow-hidden border border-[#D2C2B3] relative z-0">
          <MapContainer center={center} zoom={5} className="h-full w-full">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds positions={entries.map(e => [e.latitude, e.longitude])} />
            {entries.map(entry => (
              <Marker key={entry.id} position={[entry.latitude, entry.longitude]}>
                <Popup>
                  <div
                    className="cursor-pointer"
                    onClick={() => navigate(`/entry/${entry.id}`)}
                  >
                    <h3 className="font-semibold">{entry.title}</h3>
                    <p className="text-sm text-gray-600">{entry.location}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(entry.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      )}
    </div>
  );
};

export default MapPage;
